import React from 'react';
import { Sun, Droplets, Wind, Battery, BatteryCharging, TreePine } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { renewableServices } from '../mockData';

const iconMap = {
  Sun,
  Droplets,
  Wind,
  Battery,
  BatteryCharging,
  TreePine
};

const RenewableEnergy = () => {
  return (
    <section id="renewable" className="py-24 bg-gradient-to-br from-green-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center space-x-3 mb-6">
            <Sun className="w-10 h-10 text-amber-500 animate-pulse" />
            <Wind className="w-10 h-10 text-blue-500" />
            <Droplets className="w-10 h-10 text-cyan-500" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Power Your Homestead, Naturally ⚡
          </h2>
          <div className="h-1 w-24 bg-gold mx-auto mb-6"></div>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Ready to break free from the grid? We're your local experts in solar, mini-hydro, and micro-wind 
            systems for Kootenay homesteads and farms. Mother Nature provides the power—we make it happen!
          </p>
        </div>

        {/* Features Banner */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 mb-16 shadow-xl transform hover:scale-105 transition-transform duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-white text-center">
            <div className="space-y-2">
              <div className="text-5xl font-bold">24/7</div>
              <div className="text-green-100 text-lg">Always On Power</div>
              <div className="text-green-200 text-sm">Keep the lights on during outages, storms & grid failures</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold">100%</div>
              <div className="text-green-100 text-lg">Clean & Green</div>
              <div className="text-green-200 text-sm">Zero emissions, all awesome</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold">💰</div>
              <div className="text-green-100 text-lg">Rebates Available</div>
              <div className="text-green-200 text-sm">Ask about BC Hydro & Gov.ca rebates toward a cleaner future</div>
            </div>
          </div>
        </div>

        {/* Renewable Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {renewableServices.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card 
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-green-300 hover:-translate-y-2 bg-white"
              >
                <CardHeader className="text-center p-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-blue-100 mx-auto mb-6 group-hover:from-green-200 group-hover:to-blue-200 transition-all group-hover:rotate-12">
                    <IconComponent className="w-10 h-10 text-green-700" />
                  </div>
                  <CardTitle className="text-2xl mb-3 text-black group-hover:text-green-700 transition-colors">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RenewableEnergy;
